import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';

interface CodelistItem {
  id: number;
  code: string;
  name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
}

interface UseCodelistResult {
  items: CodelistItem[];
  loading: boolean;
  error: string | null;
  getItemByCode: (code: string) => CodelistItem | undefined;
  getItemName: (code: string) => string;
  refresh: () => Promise<void>;
}

export const useCodelist = (codelistType: string): UseCodelistResult => {
  const [items, setItems] = useState<CodelistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadItems = async () => {
    if (!codelistType) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get<{ success: boolean; data: CodelistItem[] }>(`/codelists/${codelistType}`);
      if (response.success) {
        setItems(response.data);
      } else {
        setError('Failed to load codelist items');
      }
    } catch (err: any) {
      console.error(`Failed to load codelist ${codelistType}:`, err);
      setError(err.message || 'Failed to load codelist items');
      setItems([]); // Reset items on error
    } finally {
      setLoading(false);
    }
  };

  // Load items when codelistType changes
  useEffect(() => {
    loadItems();
  }, [codelistType]);

  const getItemByCode = (code: string): CodelistItem | undefined => {
    return items.find(item => item.code === code);
  };

  const getItemName = (code: string): string => {
    const item = getItemByCode(code);
    return item ? item.name : code; // Fallback to code if item not found
  };

  const refresh = async () => {
    await loadItems();
  };

  return {
    items,
    loading,
    error,
    getItemByCode,
    getItemName,
    refresh
  };
};

// Hook for multiple codelists at once
export const useMultipleCodelists = (codelistTypes: string[]) => {
  const [codelists, setCodelists] = useState<Record<string, CodelistItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create a stable key based on contents, not array identity
  const typesKey = useMemo(() => {
    const uniqueSorted = Array.from(new Set(codelistTypes)).sort();
    return JSON.stringify(uniqueSorted);
  }, [codelistTypes]);

  useEffect(() => {
    let isCancelled = false;

    const loadAllCodelists = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const requestedTypes: string[] = JSON.parse(typesKey);
        const results: Record<string, CodelistItem[]> = {};
        
        // Load all codelists in parallel
        const promises = requestedTypes.map(async (type) => {
          try {
            const response = await api.get<{ success: boolean; data: CodelistItem[] }>(`/codelists/${type}`);
            if (response.success) {
              results[type] = response.data;
            } else {
              results[type] = [];
            }
          } catch (err) {
            console.error(`Failed to load codelist ${type}:`, err);
            results[type] = [];
          }
        });

        await Promise.all(promises);
        if (!isCancelled) {
          setCodelists(results);
        }
      } catch (err: any) {
        console.error('Failed to load codelists:', err);
        if (!isCancelled) {
          setError(err.message || 'Failed to load codelists');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    if (typesKey && JSON.parse(typesKey).length > 0) {
      loadAllCodelists();
    }

    return () => {
      isCancelled = true;
    };
  }, [typesKey]);

  const getItemName = (codelistType: string, code: string): string => {
    const items = codelists[codelistType] || [];
    const item = items.find(item => item.code === code);
    return item ? item.name : code;
  };

  const getItemByCode = (codelistType: string, code: string): CodelistItem | undefined => {
    const items = codelists[codelistType] || [];
    return items.find(item => item.code === code);
  };

  const getCodelistItems = (codelistType: string): CodelistItem[] => {
    return codelists[codelistType] || [];
  };

  return {
    codelists,
    loading,
    error,
    getItemName,
    getItemByCode,
    getCodelistItems
  };
};
